import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { toast } from "sonner";
import { Plane, Building2, Car, Package, MapPin, CheckCircle2, PowerOff } from "lucide-react";

/**
 * Service Catalog — central master-switch panel for every B2C product.
 * Disabling a service immediately removes it from the public site (navbar,
 * home page tiles) and blocks direct URL access via the SiteServiceGuard.
 */
const SERVICES = [
  { key: "product_flight",      name: "Flights",     icon: Plane,
    desc: "Real-time flight search & booking (Amadeus, NDC-X)", routes: ["/flights"] },
  { key: "product_hotel",       name: "Hotels",      icon: Building2,
    desc: "Hotel rooms, photos, ratings, instant booking", routes: ["/hotels"] },
  { key: "product_car",         name: "Cars",        icon: Car,
    desc: "Car rentals with insurance & extras", routes: ["/cars"] },
  { key: "product_package",     name: "Packages",    icon: Package,
    desc: "Holiday packages (flight + hotel bundles)", routes: ["/packages"] },
  { key: "product_sightseeing", name: "Sightseeing", icon: MapPin,
    desc: "Tours, activities, attractions & experiences", routes: ["/sightseeing"] },
];

export default function AdminServiceCatalog() {
  const { reload } = useSettings();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/settings");
      setState(data || {});
    })();
  }, []);

  const toggle = async (key) => {
    if (!state) return;
    setBusy(key);
    const next = !(state[key] !== false);
    try {
      const { data } = await api.patch("/admin/settings", { [key]: next });
      setState(data || { ...state, [key]: next });
      toast.success(`${SERVICES.find(s => s.key === key).name} ${next ? "enabled" : "disabled"}`);
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(null);
    }
  };

  if (!state) {
    return <div className="text-slate-400 py-10 text-center">Loading service catalog…</div>;
  }

  const enabledCount = SERVICES.filter(s => state[s.key] !== false).length;

  return (
    <div className="space-y-6" data-testid="admin-service-catalog">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Service Catalog</h1>
          <p className="text-slate-500 mt-1">
            Master switch for every B2C product. Turn a service off and it disappears
            from the public site instantly — navbar, home tiles, and direct URLs.
          </p>
        </div>
        <div className="text-sm text-slate-500">
          <span className="font-bold text-[var(--brand-primary-dark)]">{enabledCount}</span> of {SERVICES.length} services live
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SERVICES.map((s) => {
          const Icon = s.icon;
          const on = state[s.key] !== false;
          return (
            <Card
              key={s.key}
              data-testid={`svc-${s.key}`}
              className={
                "p-5 transition-all relative overflow-hidden " +
                (on
                  ? "bg-gradient-to-br from-white to-teal-50/40 border-teal-200/80"
                  : "bg-slate-50 border-slate-200 opacity-80")
              }
            >
              {/* Accent stripe */}
              <div className={
                "absolute top-0 left-0 right-0 h-1 " +
                (on ? "bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-primary-dark)]" : "bg-slate-300")
              } />

              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={
                    "w-12 h-12 rounded-2xl flex items-center justify-center flex-none " +
                    (on ? "bg-teal-100 text-[var(--brand-primary-dark)]" : "bg-slate-200 text-slate-500")
                  }>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading text-lg font-bold text-[var(--brand-accent)] flex items-center gap-2">
                      {s.name}
                      {on
                        ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                            <CheckCircle2 className="w-3 h-3" /> Live
                          </span>
                        : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-200/70 border border-slate-300 px-1.5 py-0.5 rounded-full">
                            <PowerOff className="w-3 h-3" /> Off
                          </span>
                      }
                    </div>
                    <div className="text-xs text-slate-500 mt-1 leading-snug">{s.desc}</div>
                    <div className="text-[10px] text-slate-400 font-mono mt-1.5">{s.routes.join(" · ")}</div>
                  </div>
                </div>

                <Switch
                  checked={on}
                  disabled={busy === s.key}
                  onCheckedChange={() => toggle(s.key)}
                  data-testid={`svc-toggle-${s.key}`}
                  className="data-[state=checked]:bg-[var(--brand-primary)]"
                />
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-4 bg-amber-50/60 border-amber-200/80 text-sm text-amber-900">
        <strong>How it works:</strong> Disabling a service hides it from the navbar &amp; home page,
        and blocks direct URL access — visitors are redirected to the home page with a notice.
        Bookings already in progress for a disabled service continue to work in the backoffice.
      </Card>
    </div>
  );
}
